/**
 * The Dashboard's "Open findings" and "Critical open" tiles.
 *
 * When /findings/counts failed, the tiles used to read 0 and "none critical"
 * (`?? 0`): a DPO would read a failed request as a clean estate (review
 * 2026-09-22, item 9). A number is shown only when it was counted.
 */
import type { FindingTierCounts } from '@/lib/api';
import type { FindingCounts } from '@/lib/fleet-api';
import { openTotals } from '@/lib/finding-tier';

export interface CountTile {
  /** The count, when there is one (KpiCard needs a number; ignored while `unavailable` is set). */
  value: number;
  /** Shown instead of the number: "..." while loading, "Unknown" after a failure. */
  unavailable?: string;
  hint: string;
}

/**
 * Release 3.3 B4: when the counts by product tier are available, the tiles
 * count the headline tiers (Confirmed + Likely) and the hint names the
 * Possible findings left out; otherwise every tier is counted and the hint
 * says so.
 */
export function openFindingTiles(
  counts: FindingCounts | null,
  loading: boolean,
  tierCounts: FindingTierCounts | null = null,
): { open: CountTile; critical: CountTile; criticalOpen: number | null } {
  if (tierCounts && tierCounts.available) {
    const head = openTotals(tierCounts.headline);
    const possible = openTotals(tierCounts.byTier.possible).all;
    const extra = possible > 0 ? ` · ${possible} possible not counted` : '';
    return {
      open: { value: head.all, hint: `${head.critical > 0 ? `${head.critical} critical` : 'none critical'}${extra}` },
      critical: { value: head.critical, hint: 'confirmed or likely; needs action now' },
      criticalOpen: head.critical,
    };
  }
  if (!counts) {
    const unavailable = loading ? '...' : 'Unknown';
    const hint = loading ? 'counting' : 'could not be counted';
    return {
      open: { value: 0, unavailable, hint },
      critical: { value: 0, unavailable, hint },
      criticalOpen: null,
    };
  }
  const open = counts.open.total;
  const critical = counts.open.critical;
  // No counts by tier (too large to count per request, or the request
  // failed): these are every tier, and the tiles say so (review of PR #172, S5).
  const allTiers = ' (all tiers)';
  return {
    open: { value: open, hint: `${critical > 0 ? `${critical} critical` : 'none critical'}${allTiers}` },
    critical: { value: critical, hint: 'all tiers; needs action now' },
    criticalOpen: critical,
  };
}
